import React, { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useCheckout } from "@stripe/react-stripe-js"

import PaywallToggle from "./PaywallToggle"
import PaywallCheckoutWidget from "./PaywallCheckoutWidget"
import {
  useAccount,
  useAccountId,
  useUpdateStripeCheckoutSession,
} from "../../hooks"
import { formatPrice } from "../../shared/utils"
import { AccountTypes } from "@/types/account"

export interface PaywallPaymentFormProps {
  joinBtnClass?: string
}

const PaywallPaymentForm = ({ joinBtnClass }: PaywallPaymentFormProps) => {
  const queryClient = useQueryClient()
  const accountId = useAccountId()
  const { runServerUpdate, id: stripeSessionId } = useCheckout()
  const [isMonthly, setIsMonthly] = useState<boolean>(true)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  const { data: account, isLoading: accountIsLoading } = useAccount<
    AccountTypes,
    Error
  >(queryClient, accountId)

  const { mutateAsync: updateStripeSession } = useUpdateStripeCheckoutSession(
    queryClient,
    accountId
  )

  const updateCheckoutSession = (isMonthly: boolean) => {
    return updateStripeSession({
      lookup_key: isMonthly ? "basic_monthly" : "basic_annual",
      session_id: stripeSessionId,
    })
  }

  const handleIsMonthly = async () => {
    // update checkout session with new selection and update form components
    return await runServerUpdate(() => updateCheckoutSession(!isMonthly))
  }

  const handleUpdateFrequency = () => {
    setIsUpdating(true)
    handleIsMonthly().then((result) => {
      if (result.type === "success") {
        // Update toggle state on success
        setIsMonthly((oldIsMonthly) => !oldIsMonthly)
      }
      setIsUpdating(false)
    })
  }

  if (accountIsLoading || !account) {
    return null
  }

  const priceYearly = formatPrice(account?.subscription_pricing.basic_annual)
  const priceMonthly = formatPrice(account?.subscription_pricing.basic_monthly)
  const priceMonthAnnual = formatPrice(
    account?.subscription_pricing.basic_annual / 12
  )

  return (
    <>
      {isUpdating && (
        <div className="text-dusk-300 font-medium">Updating...</div>
      )}
      <PaywallToggle
        selection={isMonthly}
        setSelection={() => handleUpdateFrequency()}
      />

      <PaywallCheckoutWidget
        price={isMonthly ? priceMonthly : priceMonthAnnual}
        isMonthly={isMonthly}
        priceYearly={priceYearly}
        joinBtnClass={joinBtnClass}
      />
    </>
  )
}

export default PaywallPaymentForm
